import { FC, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { List } from "@/components/List";
import { Loader2 } from "lucide-react";
import { IAndITimePeriodsValue } from "@/config/date";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { getIAndIv1Columns } from "./Columns";
import { ExpandColumn } from "@/components/tableColumns/ExpandColumn";
import { infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";
import { getColumnUnits, IAndIListColumnApis } from "./useIAndIListColumnMaxes";

interface ExpandedIAndIMembersProps {
	downstreamStructureId: Dryp.StructureId;
	downstreamStructureName: string;
	timePeriod: IAndITimePeriodsValue;
	columnApis: IAndIListColumnApis;
}

// Recursive drill-down panel: fetches the structures flowing directly into
// `downstreamStructureId` and renders them as the same I&I list. Each nested row
// can be expanded again to go one level deeper upstream.
export const ExpandedIAndIMembers: FC<ExpandedIAndIMembersProps> = observer(
	({ downstreamStructureId, downstreamStructureName, timePeriod, columnApis }) => {
		const iandiApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);

		useEffect(() => {
			iandiApi.fetch({ pageNumber: 0, pageSize: 1000 }, timePeriod, undefined, {
				downstreamStructureId,
			});
		}, [downstreamStructureId, timePeriod]);

		const iandi = iandiApi.data?.items || [];

		const { volume: volumeUnit, flowPerLength: flowPerLengthUnit } = getColumnUnits(iandi);

		const columns = useMemo<ColumnDef<Dryp.IAndISummary, any>[]>(
			() => [
				ExpandColumn<Dryp.IAndISummary>(),
				...getIAndIv1Columns(columnApis, { volume: volumeUnit, flowPerLength: flowPerLengthUnit }),
			],
			[columnApis, volumeUnit, flowPerLengthUnit],
		);

		const table = useReactTable({
			data: iandi,
			columns,
			getCoreRowModel: getCoreRowModel(),
			getRowId: iandiRow => String(iandiRow.structure.id),
			initialState: {
				columnVisibility: {
					structureId: false,
					createdAt: false,
					"structure.networkType": false,
				},
			},
		});

		if (iandiApi.loading && !iandiApi.loaded) {
			return (
				<div className="bg-muted/20 py-4 pl-3 pr-3 shadow-[inset_4px_0_0_0_hsl(var(--primary)/0.4),inset_0_9px_7px_-8px_rgba(0,0,0,0.18),inset_0_-9px_7px_-8px_rgba(0,0,0,0.18)]">
					<div className="flex items-center gap-2 text-sm font-medium text-primary">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading upstream structures…
					</div>
				</div>
			);
		}

		if (iandi.length === 0) {
			return (
				<div className="bg-muted/20 py-3 pl-3 pr-3 text-sm text-muted-foreground shadow-[inset_4px_0_0_0_hsl(var(--primary)/0.4),inset_0_9px_7px_-8px_rgba(0,0,0,0.18),inset_0_-9px_7px_-8px_rgba(0,0,0,0.18)]">
					No upstream structures flow directly into {downstreamStructureName}.
				</div>
			);
		}

		return (
			<div className="relative bg-muted/20 py-3 shadow-[inset_0_9px_7px_-8px_rgba(0,0,0,0.18),inset_0_-9px_7px_-8px_rgba(0,0,0,0.18)]">
				<div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-1 bg-primary/50" />
				<div
					className="pointer-events-none absolute left-0 top-0 z-20 h-4 w-4 bg-primary/60"
					style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
				/>
				<List
					pagination={undefined}
					table={table}
					layout={{
						loading: false,
						hideHeader: true,
						tableWrapperClassName: "rounded-none border-none bg-transparent",
					}}
					expandedRowPanel={iAndiRow => (
						<ExpandedIAndIMembers
							downstreamStructureId={iAndiRow.original.structure.id}
							downstreamStructureName={iAndiRow.original.structure.name}
							timePeriod={timePeriod}
							columnApis={columnApis}
						/>
					)}
				/>
			</div>
		);
	},
);
